import React, {
  ChangeEventHandler,
  forwardRef,
  useMemo,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl';

import { ReservationFlowCustomerFieldItem } from 'src/interfaces/reservation_flow';
import { CustomerTextField } from 'src/templates/merchants/resources/book/components/forms/CustomerTextField';
import { useDataAttributeProps } from 'src/hooks/useDataAttributeProps';

export interface CustomerInfoInputTextFieldProps {
  [key: `data-${string}`]: string | undefined;
  value?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  item: ReservationFlowCustomerFieldItem;
  isError?: boolean;
}

export const CustomerInfoInputTextField = forwardRef<
  HTMLInputElement,
  CustomerInfoInputTextFieldProps
>((props, ref) => {
  const intl = useIntl();

  const dataAttributes = useDataAttributeProps(props);
  const { value, onChange, onBlur, item, isError } = props;

  const validationTypeString = useMemo<string | undefined>(() => {
    // デフォルト項目のうち、メールアドレスと電話番号、名前(カナ)はバリデーションタイプの表示をしない
    if (
      item.key === 'email' ||
      item.key === 'phone_number' ||
      item.key === 'last_name_kana' ||
      item.key === 'first_name_kana'
    ) {
      return undefined;
    }

    switch (item.validation_type) {
      case 'alphanumeric':
        return intl.formatMessage({
          defaultMessage: '（英数字のみ）',
          id: 'lE90OS',
          description:
            'src/templates/merchants/resources/book/components/customer-info/CustomerInfoInputTextField/index.tsx',
        });
      case 'email':
        return intl.formatMessage({
          defaultMessage: '（メールアドレス）',
          id: 'qC4I7o',
          description:
            'src/templates/merchants/resources/book/components/customer-info/CustomerInfoInputTextField/index.tsx',
        });
      case 'katakana':
        return intl.formatMessage({
          defaultMessage: '（カタカナのみ）',
          id: 'fUMOhM',
          description:
            'src/templates/merchants/resources/book/components/customer-info/CustomerInfoInputTextField/index.tsx',
        });
      case 'numeric':
        return intl.formatMessage({
          defaultMessage: '（数字のみ）',
          id: '7GHzQ8',
          description:
            'src/templates/merchants/resources/book/components/customer-info/CustomerInfoInputTextField/index.tsx',
        });
      case 'phone':
        return intl.formatMessage({
          defaultMessage: '（電話番号）',
          id: '00bqbC',
          description:
            'src/templates/merchants/resources/book/components/customer-info/CustomerInfoInputTextField/index.tsx',
        });
      default:
        return undefined;
    }
  }, [intl, item.key, item.validation_type]);

  const onInputChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (event) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  return (
    <div>
      <CustomerTextField
        {...dataAttributes}
        ref={ref}
        labelText={
          typeof validationTypeString === 'undefined'
            ? item.label
            : `${item.label}${validationTypeString}`
        }
        isBlock
        isError={isError}
        type={item.input_type !== null ? item.input_type : 'text'}
        required={item.is_required}
        value={typeof value !== 'undefined' ? value : ''}
        onChange={onInputChange}
        onBlur={onBlur}
      />
    </div>
  );
});

CustomerInfoInputTextField.displayName = 'CustomerInfoInputTextField';
